import { newIdentifier } from '@cadet-dsl';
import type { Doc } from '@doc';
import { createNodeMutation } from '@doc';
import type { Point } from '@geom';
import { Elements, type NID } from '@graph';
import type { CommandContext } from './CommandContext';
import { revealBounds } from './zoom.commands';

export function canGroupSelection(context: CommandContext): boolean {
  return !context.selection.isEmpty;
}

export interface GroupSelectionResult {
  doc: Doc;
  selection: Elements;
  newGroup: NID;
  pan: Point;
  zoom: number;
}

export function groupSelection(
  cls: string | undefined,
  context: CommandContext
): GroupSelectionResult | undefined {
  if (!canGroupSelection(context)) {
    return;
  }
  let { doc, selection } = context;
  const { layouter } = context;

  let newGroup!: NID;

  const oldDoc = doc;

  try {
    const nodes = selection.nodes;

    // Edges that are selected include their head and tail nodes
    for (const edge of selection.edges) {
      nodes.add(doc.graph.source(edge));
      nodes.add(doc.graph.target(edge));
    }

    const leastCommonAncestor = doc.graph.leastCommonAncestor(nodes);

    let parentOfNewGroup: NID | undefined;
    if (leastCommonAncestor === undefined) {
      parentOfNewGroup = undefined;
    } else {
      if (nodes.has(leastCommonAncestor)) {
        parentOfNewGroup = doc.graph.parent(leastCommonAncestor);
      } else {
        parentOfNewGroup = leastCommonAncestor;
      }
    }

    const nodesToReparent = new Set<NID>();
    if (parentOfNewGroup === undefined) {
      for (const node of nodes) {
        const pathToRoot = doc.graph.pathToRoot(node);
        // get the last item
        const topGroup = pathToRoot[pathToRoot.length - 1];
        nodesToReparent.add(topGroup);
      }
    } else {
      for (const node of nodes) {
        const pathToRoot = doc.graph.pathToRoot(node);
        const indexOfParent = pathToRoot.indexOf(parentOfNewGroup);
        const pathToParent = pathToRoot.slice(0, indexOfParent);
        const topGroup = pathToParent[pathToParent.length - 1];
        nodesToReparent.add(topGroup);
      }
    }

    doc = doc.produce('groupSelection', draft => {
      const doc = draft as Doc;
      newGroup = createNodeMutation(
        doc,
        cls,
        true,
        layouter.cache,
        undefined,
        parentOfNewGroup
      );
      for (const node of nodesToReparent) {
        const inEdge = doc.graph.inEdge(node);
        if (inEdge !== undefined) {
          doc.graph.moveNode(node, { newParent: newGroup });
        } else {
          doc.graph.moveNode(node, {
            newParent: newGroup,
            newEdge: newIdentifier()
          });
        }
      }
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    selection = Elements.make([newGroup]);
    const { pan, zoom } = revealBounds(doc.nodeLayout(newGroup).bounds, context);
    const result = { doc, selection, newGroup, pan, zoom };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}
